/**
 * Dashboard API Service
 * API functions for dashboard stats and charts
 */
import { useQuery } from '@tanstack/react-query'
import { apiClient } from './client'
import type { EstimateMessage } from '@/types/estimate'
import type { ClientCall } from '@/types/clientCall'
import type { TelegramProxyProblem } from '@/types/telegramAccount'
import type { ManagerBrief } from '@/types/manager'
import type { NextBotTouch } from '@/types/nextBotTouch'
import type { ClientCompanyRef } from '@/types/clientCompany'
import type { AttentionSection, AttentionSectionCounts } from '@/lib/attentionSections'

/**
 * Recent estimate item in dashboard stats
 */
export interface RecentEstimate {
  id: string
  query: string
  total_cost: string
  status: string
  created_at: string
}

/**
 * Dashboard statistics response
 */
export interface DashboardStats {
  total_estimates: number
  data_sources: number
  users_count: number
  monthly_cost: number
  recent_estimates: RecentEstimate[]
}

/**
 * Estimates trend data point
 */
export interface EstimatesTrendPoint {
  date: string
  count: number
}

/**
 * Status distribution item
 */
export interface StatusDistributionItem {
  name: string
  value: number
  color: string
}

/**
 * Dashboard charts data response
 */
export interface DashboardCharts {
  estimates_trend: EstimatesTrendPoint[]
  status_distribution: StatusDistributionItem[]
}

/**
 * Communication channel of a message (TASK-208)
 */
export type MessageSource = 'web' | 'telegram' | 'voice' | 'email' | 'max'

/**
 * Client info attached to a contact (TASK-208)
 */
export interface ContactClient {
  /** KAN-626: client id (manual chat mode); absent on an older backend. */
  id?: string
  manual_mode?: boolean
  name: string | null
  phone: string | null
  telegram_id: string | null
}

/**
 * Last message snippet of a contact dialog (TASK-208)
 */
export interface ContactLastMessage {
  content: string
  source: MessageSource
  role: 'user' | 'assistant'
  created_at: string
}

/**
 * Contact = estimate with a client dialog (TASK-208)
 */
export interface Contact {
  id: string
  display_name: string
  client: ContactClient | null
  last_message: ContactLastMessage | null
  /** KAN-645: our Telegram account talking to the client; absent on an older backend. */
  sender_account?: AttentionSenderAccount | null
  messages_count: number
  total_cost: string
  status: string
  updated_at: string
}

/**
 * Fetch dashboard statistics
 */
export async function getDashboardStats(): Promise<DashboardStats> {
  const response = await apiClient.get<DashboardStats>('/api/v1/dashboard/stats/')
  return response.data
}

/**
 * Fetch dashboard charts data
 */
export async function getDashboardCharts(): Promise<DashboardCharts> {
  const response = await apiClient.get<DashboardCharts>('/api/v1/dashboard/charts/')
  return response.data
}

/**
 * Fetch recent client contacts (dialogs) - TASK-208
 */
export async function getDashboardContacts(): Promise<Contact[]> {
  const response = await apiClient.get<Contact[]>('/api/v1/dashboard/contacts/')
  return response.data
}

/**
 * Fetch the full transcript of a contact dialog - TASK-208
 *
 * Org-scoped endpoint: unlike `GET /estimates/{id}/messages/` (per-user, 404s
 * for contacts owned by another operator), this returns messages for any
 * estimate within the organization. Response format is identical.
 */
export async function getContactMessages(
  estimateId: string
): Promise<EstimateMessage[]> {
  const response = await apiClient.get<EstimateMessage[]>(
    `/api/v1/dashboard/contacts/${estimateId}/messages/`
  )
  return response.data
}

/** KAN-654: max messages of the client feed loaded into the transcript. */
export const CLIENT_MESSAGES_LIMIT = 500

/**
 * KAN-654: all messages of a client in one feed, oldest first.
 *
 * A client may have several estimates-dialogs (different our Telegram
 * accounts, the old userbot, manual sends); this endpoint merges them. Items
 * have the shape of `getContactMessages` plus `estimate_id`.
 */
export async function getClientMessages(
  clientId: string,
  /** KAN-834: the latest `limit` messages (the manager app shows 30). */
  limit: number = CLIENT_MESSAGES_LIMIT
): Promise<EstimateMessage[]> {
  const response = await apiClient.get<EstimateMessage[]>(
    `/api/v1/dashboard/clients/${clientId}/messages/`,
    { params: { limit } }
  )
  return response.data
}

/**
 * KAN-929: calls of a manager with the client (and inbound calls a manager
 * took), oldest first, for the conversation feed. Bot calls are not here: they
 * are already messages of the feed. No ?limit: the list is short.
 */
export async function getClientCalls(clientId: string): Promise<ClientCall[]> {
  const response = await apiClient.get<ClientCall[]>(
    `/api/v1/dashboard/clients/${clientId}/calls/`
  )
  return response.data
}

/** KAN-628: why a client needs a human. */
export type AttentionReason =
  | 'handed_to_manager'
  | 'needs_human'
  | 'dm_identified'
  | 'manual_mode'
  | 'unanswered'
  | 'dm_reached'
  | 'interested'
  | 'replied'
  /** KAN-774: all nudges sent, the client is silent; a human calls. */
  | 'nudge_exhausted'
  /** KAN-774: the client replied once and went silent after the follow-ups. */
  | 'replied_silent'
  /** KAN-919: the client declined the product, the product_declined ladder is exhausted (lower tier). */
  | 'product_declined'
  /** KAN-783: a configured lead attribute with the attention flag has a value. */
  | 'lead_attribute'
  /** KAN-790: a nudge ladder step set a task for the manager. */
  | 'manager_task'
  /**
   * KAN-890, KAN-907: an open lead with a manager or taken to work without messages,
   * calls and a scheduled callback for 7 days ("Нет движения").
   */
  | 'idle'
  /** KAN-926: the client was returned to work from an outcome ("Возвращён в работу"). */
  | 'reopened'

/** KAN-790: the manager task of an attention row (reason manager_task). */
export interface AttentionTask {
  text: string
  /** ISO time the task is due. */
  due_at: string
  overdue: boolean
}

/** KAN-783: a configured lead attribute of the client with a non-empty value. */
export interface AttentionAttribute {
  name: string
  value: string
}

/** KAN-628: our Telegram account the client talks to (as in the clients list). */
export interface AttentionSenderAccount {
  account_id: string
  label?: string
  phone?: string
  username?: string
  persona_name?: string
  profile_name?: string
  avatar_url?: string
}

/** KAN-628: one client awaiting a human. */
export interface AttentionItem {
  client_id: string
  /** KAN-706: where the client talks to us. */
  channel?: AttentionChannel
  name: string
  company: string
  /** KAN-826: organization of the client; null: none; absent on an older backend. */
  client_company?: ClientCompanyRef | null
  reasons: AttentionReason[]
  reason_text: string
  since: string
  last_message: {
    content: string
    source: MessageSource
    created_at: string
  } | null
  sender_account: AttentionSenderAccount | null
  estimate_id: string | null
  /** KAN-734: when "Отработано" was pressed (only in the worked view). */
  attention_done_at?: string | null
  /** KAN-750: sales manager of the client (default in the "Отработано" dialog). */
  manager?: ManagerBrief | null
  /** KAN-774: client phone for the "Позвонить" link; absent on an older backend. */
  phone?: string | null
  /** KAN-774: what the bot offered, "новый факт (11.10)"; absent on an older backend. */
  offered?: string[]
  /** KAN-783: configured lead attributes with values; absent on an older backend. */
  attributes?: AttentionAttribute[]
  /** KAN-810: lead temperature hot | warm | cool; "" none; absent on an older backend. */
  lead_temperature?: string
  /** KAN-790: the manager task from the nudge ladder; absent on an older backend. */
  task?: AttentionTask | null
  /** KAN-815: the next bot touch; null: none planned; absent on an older backend. */
  next_bot_touch?: NextBotTouch | null
  /** KAN-857: client Telegram avatar url (needs the token); "" none; absent on an older backend. */
  avatar_url?: string
  /** KAN-911: when the client was taken to work (ISO); null: not taken; absent on an older backend. */
  taken_at?: string | null
  /** KAN-960: текущий шаг на карточке (ISO, код, подпись); null / "" - шага нет. */
  followup_at?: string | null
  followup_type?: string
  followup_label?: string
  /**
   * KAN-918: last client message newer than taking to work and the last manual
   * manager message (ISO, worked view); null: none; absent on an older backend.
   */
  new_message_at?: string | null
  /** KAN-926: section of the attention list (new / stale / declined / reopened); absent on an older backend. */
  attention_section?: AttentionSection | null
}

export interface AttentionList {
  total: number
  items: AttentionItem[]
  /** KAN-706: counts per channel, independent of the `channel` filter. */
  totals?: AttentionTotals
  /**
   * KAN-794: open clients who wrote to us but have no planned touch, no
   * attention row and no outcome. Normally 0; absent on an older backend.
   */
  no_next_step?: number | null
  /**
   * KAN-926: rows per attention section over the whole list (before the limit);
   * absent on an older backend.
   */
  section_counts?: AttentionSectionCounts | null
}

/** KAN-706: channel of an attention row (tab on the dashboard). */
export type AttentionChannel = 'telegram' | 'call'

export type AttentionTotals = Record<AttentionChannel, number>

export interface AttentionParams {
  /** KAN-706: only rows of this channel. */
  channel?: AttentionChannel
  /** KAN-705: 1..200, backend default 20. */
  limit?: number
}

/**
 * KAN-628: clients that need a human (handed to manager, flagged, DM found).
 * KAN-705: optional `limit` to fetch more rows. KAN-706: optional `channel`.
 */
export async function getAttention(params: AttentionParams = {}): Promise<AttentionList> {
  return fetchAttention(params)
}

/**
 * KAN-734: clients marked "Отработано" whose outcome is not set yet. Same
 * response shape as getAttention, rows also carry `attention_done_at`.
 */
export async function getWorkedAttention(params: AttentionParams = {}): Promise<AttentionList> {
  return fetchAttention(params, { view: 'worked' })
}

async function fetchAttention(
  params: AttentionParams,
  extra: Record<string, string> = {}
): Promise<AttentionList> {
  const query: Record<string, string | number> = { ...extra }
  if (params.channel !== undefined) query.channel = params.channel
  if (params.limit !== undefined) query.limit = params.limit
  const response = await apiClient.get<AttentionList>(
    '/api/v1/dashboard/attention/',
    Object.keys(query).length ? { params: query } : undefined
  )
  return response.data
}

/**
 * KAN-628: mark a client as handled (does not reset needs_human).
 * KAN-750: `managerId` (uuid or null) also assigns or removes the client's
 * manager; left out, the manager is not touched. A wrong manager is 400
 * {"manager_id": [...]} and the client is not marked.
 */
export async function markAttentionDone(
  clientId: string,
  managerId?: string | null
): Promise<void> {
  const url = `/api/v1/dashboard/attention/${clientId}/done/`
  if (managerId === undefined) {
    await apiClient.post(url)
  } else {
    await apiClient.post(url, { manager_id: managerId })
  }
}

/** KAN-726: outcome a human sets from the attention block. */
export type AttentionOutcome = 'success' | 'refused'

/**
 * KAN-787: action on an attention row: neutral close or an outcome.
 * KAN-955: work_continues ("Работа идёт") on a row with the idle reason.
 */
export type AttentionAction = 'done' | AttentionOutcome | 'work_continues'

/**
 * KAN-726: "Успех" moves the client to the won stage, "Отказ" to the lost one
 * (the bot stops nudging); both hide the row like "Отработано".
 */
export async function setAttentionOutcome(
  clientId: string,
  outcome: AttentionOutcome
): Promise<void> {
  await apiClient.post(`/api/v1/dashboard/attention/${clientId}/outcome/`, { outcome })
}

/**
 * KAN-955: "Работа идёт" on a row with the idle reason ("Нет движения"): the row
 * leaves attention for IDLE_DAYS days, taking, "Отработано" and the outcome
 * stay as they are. No idle reason now: 409 {"detail"}.
 */
export async function markWorkContinues(clientId: string): Promise<void> {
  await apiClient.post(`/api/v1/dashboard/attention/${clientId}/work-continues/`)
}

/** KAN-643: state of one outreach account today. */
export type OutreachAccountState =
  | 'writing'
  | 'cooldown'
  | 'limit'
  | 'peer_flood'
  | 'dead'
  /** KAN-719: no connection and the account proxy failed its last check. */
  | 'proxy_down'
  | 'disabled'
  | 'decommissioned'
  | 'outside_window'

export interface OutreachAccount {
  id: string
  operator_id: string
  label: string
  contour: 'gateway' | 'legacy'
  sent_today: number
  limit: number
  state: OutreachAccountState
  until: string | null
  next_send_at: string | null
  detail: string
  /** KAN-719: set for proxy_down; absent on an older backend. */
  proxy_problem?: TelegramProxyProblem | null
}

export interface OutreachWindow {
  start: string
  end: string
  timezone: string
  open: boolean
  opens_at: string | null
  /** KAN-764: send days, ISO 1=Mon..7=Sun; absent on an older backend. */
  days?: number[]
}

export interface OutreachCampaignQueue {
  id: string
  name: string
  new: number
  pending: number
  claimed: number
}

export interface OutreachService {
  name: 'beat' | 'planner' | 'gateway'
  last_seen: string | null
  age_s: number | null
  ok: boolean
}

/** KAN-660: one big state of the block with its reason. */
export type OutreachHeadlineState =
  | 'running'
  | 'paused'
  | 'outside_window'
  | 'limit'
  | 'idle'
  | 'error'

export interface OutreachHeadline {
  state: OutreachHeadlineState
  /** Russian text to show as is ("На паузе до 14:20 МСК: PeerFlood"). */
  text: string
  /** When the pause ends / the window opens (ISO) or null. */
  until: string | null
  /** Machine reason: peer_flood / flood_wait / paused / service_down / ... or "". */
  reason: string
}

/** KAN-664: the client a first message went to (payload client or the linked record client). */
export interface OutreachSentClient {
  id: string
  name: string
  phone: string
}

/** KAN-660: one first message sent today (a mark on the day timeline). */
export interface OutreachSentMark {
  at: string
  account_id: string
  label: string
  /** KAN-664; absent on an older backend. */
  client?: OutreachSentClient | null
  /** KAN-664: client name, else phone/username, else "получатель"; absent on an older backend. */
  recipient?: string
}

/** KAN-664: a record the planner will write to next (planner order). */
export interface OutreachPlannedRecipient {
  record_id: string
  name: string
  phone: string
  campaign: string
}

/** KAN-683: what limits today's forecast. */
export type OutreachForecastBound = 'queue' | 'accounts_limit' | 'window' | 'none'

/** KAN-683: capacity of one account for the rest of the day. */
export interface OutreachForecastAccount {
  label: string
  /** Daily limit minus sent today. */
  limit_left: number
  /** How many sends fit into the rest of the window at the operator's delays. */
  window_capacity: number
  /** min(limit_left, window_capacity). */
  will: number
}

/** KAN-683: how many queue records will be processed today and what bounds it. */
export interface OutreachForecast {
  will_send: number
  queue_total: number
  /** Sum of limit_left over counted accounts. */
  capacity_left: number
  /** Sum of window_capacity over counted accounts. */
  window_capacity: number
  bound_by: OutreachForecastBound
  avg_delay_s: number
  delay_min_s: number
  delay_max_s: number
  per_account: OutreachForecastAccount[]
  /** KAN-710: why the rest of the accounts limit will not be sent today; absent on an older backend. */
  blocked?: OutreachForecastBlocked[]
}

/** KAN-710: reason a part of today's accounts limit will not be sent. */
export type OutreachBlockedReason =
  | 'peer_flood'
  | 'flood_wait'
  | 'proxy_down'
  | 'disabled'
  | 'cooldown_past_window'
  | 'queue_empty'
  | 'other'

/** KAN-710: one reason with its accounts and lost slots (sum of slots = capacity - sent - will_send). */
export interface OutreachForecastBlocked {
  reason: OutreachBlockedReason
  /** Labels of the affected accounts (count = length, labels go to the tooltip). */
  accounts: string[]
  slots: number
}

/** KAN-643: dashboard block "Рассылка сегодня". */
export interface OutreachStatus {
  status: 'running' | 'waiting' | 'stopped'
  alert: boolean
  /** KAN-660; absent on an older backend. */
  headline?: OutreachHeadline
  window: OutreachWindow | null
  accounts: OutreachAccount[]
  progress: { sent_today: number; capacity: number }
  queue: OutreachCampaignQueue[]
  services: OutreachService[]
  /** KAN-660: sent today, ascending; absent on an older backend. */
  sent_times?: OutreachSentMark[]
  /** KAN-664: next recipients in planner order, cut by the remaining capacity; absent on an older backend. */
  planned?: OutreachPlannedRecipient[]
  /** KAN-683: today's forecast; absent on an older backend. */
  forecast?: OutreachForecast
  checked_at: string
}

/** KAN-643: is Telegram outreach running today or stuck. */
export async function getOutreachStatus(): Promise<OutreachStatus> {
  const response = await apiClient.get<OutreachStatus>('/api/v1/dashboard/outreach-status/')
  return response.data
}

export const OUTREACH_STATUS_QUERY_KEY = ['dashboard-outreach-status'] as const

/** KAN-643: outreach status, refreshed every 30 s. */
export function useOutreachStatus() {
  return useQuery<OutreachStatus>({
    queryKey: OUTREACH_STATUS_QUERY_KEY,
    queryFn: getOutreachStatus,
    refetchInterval: 30000,
  })
}

/** KAN-783: period of the sales report, Moscow dates YYYY-MM-DD, inclusive. */
export interface SalesByManagerParams {
  date_from: string
  date_to: string
}

/** KAN-783: orders and revenue of one manager; `manager` null = orders without a manager. */
export interface SalesByManagerRow {
  manager: { id: string; name: string } | null
  orders: number
  total: number
}

/** KAN-783: GET /api/v1/dashboard/sales-by-manager/ response. */
export interface SalesByManager {
  range: SalesByManagerParams
  currency: string
  /** Sorted by total, the row without a manager last. */
  rows: SalesByManagerRow[]
  total: { orders: number; total: number }
}

/**
 * KAN-783: e-commerce orders (sent for review or approved) per manager for the
 * period. 400 {"date_from": [...]} on a bad range, as the CRM board.
 */
export async function getSalesByManager(params: SalesByManagerParams): Promise<SalesByManager> {
  const response = await apiClient.get<SalesByManager>('/api/v1/dashboard/sales-by-manager/', {
    params,
  })
  return response.data
}

export const dashboardApi = {
  getStats: getDashboardStats,
  getCharts: getDashboardCharts,
  getContacts: getDashboardContacts,
  getContactMessages,
  getClientMessages,
  getClientCalls,
  getAttention,
  getWorkedAttention,
  markAttentionDone,
  setAttentionOutcome,
  markWorkContinues,
  getOutreachStatus,
  getSalesByManager,
}
